import { Loader2, AlertTriangle, RefreshCw, ExternalLink, Server } from "lucide-react";
import {
  PUMPPORTAL_MIN_LINKED_WALLET_SOL,
  isPumpPortalLinkedWalletFunded,
} from "@/lib/pumpPortalConfig";
import { useApp } from "@/context/AppCtx";
import { useState, useEffect } from "react";

import { QRCodeSVG } from "qrcode.react";

function fmtSolUi(n: number): string {
  if (n >= 100) return n.toFixed(2);
  if (n >= 1) return n.toFixed(3);
  return n.toFixed(4);
}

export function PumpPortalWalletFundingBadge({
  sol,
  loading,
  pubkey,
  compact,
}: {
  sol: number | null;
  loading: boolean;
  pubkey: string | null;
  /** Narrow pill for top chrome */
  compact?: boolean;
}) {
  const { solPrice } = useApp();
  const [showRefillOptions, setShowRefillOptions] = useState(false);
  const [heliusBalance, setHeliusBalance] = useState<number | null>(null);
  const [checkingBalances, setCheckingBalances] = useState(false);

  const funded = sol !== null && isPumpPortalLinkedWalletFunded(sol);

  // Check Helius balance when balance error occurs
  useEffect(() => {
    if (!funded && sol !== null && showRefillOptions && pubkey) {
      setCheckingBalances(true);
      
      let apiKey = "c78141ce-045d-48ab-9847-405100d54a14";
      
      fetch(`/helius-rpc/?api-key=${apiKey}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          jsonrpc: "2.0",
          id: "balance-check",
          method: "getBalance",
          params: ["placeholder"]
        })
      })
        .then(res => res.json())
        .then(data => {
          if (data.result) {
            setHeliusBalance(data.result.value / 1e9);
          }
        })
        .catch(() => {})
        .finally(() => setCheckingBalances(false));
    }
  }, [funded, sol, showRefillOptions, pubkey]);
  
  if (!pubkey) {
    return (
      <span
        className={
          "shrink-0 rounded-full border border-white/10 bg-[var(--color-fill)] px-2 py-0.5 text-[10px] font-medium text-[var(--color-fg-dim)] " +
          (compact ? "" : "max-w-[14rem]")
        }
        title="Add your PumpPortal wallet private key in Setup to show live SOL balance."
      >
        {compact ? "Portal wallet —" : "Portal wallet — add secret key in Setup"}
      </span>
    );
  }

  if (loading && sol === null) {
    return (
      <span className="flex shrink-0 items-center gap-1 rounded-full border border-white/10 bg-[var(--color-fill)] px-2 py-0.5 text-[10px] font-medium text-[var(--color-fg-muted)]">
        <Loader2 className="size-3 animate-spin opacity-70" aria-hidden />
        SOL…
      </span>
    );
  }

  if (sol === null) {
    return (
      <span
        className="shrink-0 rounded-full border border-amber-500/35 bg-amber-500/12 px-2 py-0.5 text-[10px] font-medium text-amber-100/90"
        title="Could not read balance — try another RPC (VITE_SOLANA_RPC_URL) or confirm the address."
      >
        SOL unreadable
      </span>
    );
  }

  return (
    <div className="relative">
      <span
        className={
          "shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold tabular-nums cursor-pointer hover:opacity-80 transition-opacity " +
          (funded
            ? "border border-emerald-500/35 bg-emerald-500/10 text-status-success dark:bg-emerald-500/15 dark:text-emerald-200"
            : "border border-red-500/40 bg-red-500/10 text-status-danger dark:bg-red-500/15 dark:text-red-100")
        }
        title={
          funded
            ? `Trading wallet reads ~${fmtSolUi(sol)} SOL — meets PumpPortal’s usual ~${PUMPPORTAL_MIN_LINKED_WALLET_SOL} SOL guideline (RPC rounding allowed).`
            : `Trading wallet reads ~${fmtSolUi(sol)} SOL — below PumpPortal’s usual ~${PUMPPORTAL_MIN_LINKED_WALLET_SOL} SOL floor; click for refill options.`
        }
        onClick={() => !funded && setShowRefillOptions(!showRefillOptions)}
      >
        <span className="flex items-center gap-1.5">
          <span>{fmtSolUi(sol)} SOL</span>
          <span className="opacity-40">/</span>
          <span className="text-white/80">${(sol * solPrice).toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
          <span className="opacity-20 ml-1 font-black uppercase tracking-widest">{funded ? "funded" : "low"}</span>
          {!funded && <AlertTriangle className="size-3 text-red-400 animate-pulse" />}
        </span>
      </span>

      {!funded && showRefillOptions && (
        <div className="absolute top-full right-0 mt-2 w-72 bg-neutral-900 border border-white/10 rounded-xl shadow-2xl p-3 z-50">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="size-4 text-red-400" />
            <span className="text-[10px] font-bold text-white">Insufficient Balance</span>
          </div>
          <p className="text-[9px] text-white/60 mb-3 leading-relaxed">
            Your PumpPortal wallet has {fmtSolUi(sol || 0)} SOL, which is below the recommended {PUMPPORTAL_MIN_LINKED_WALLET_SOL} SOL minimum for trading.
          </p>

          {/* QR Code for Funding */}
          {pubkey && (
            <div className="flex flex-col items-center justify-center mb-4 p-3 bg-white/5 rounded-xl border border-white/10">
              <QRCodeSVG value={pubkey} size={150} className="mb-2" />
              <span className="text-[8px] font-mono text-white/50 break-all select-all text-center">{pubkey}</span>
            </div>
          )}

          {/* Balance Check Results */}
          {heliusBalance !== null && (
            <div className="mb-3 p-2 rounded-lg bg-white/5 border border-white/10">
              <div className="flex items-center gap-2 mb-2">
                <Server className="size-3 text-emerald-400" />
                <span className="text-[9px] font-bold text-white/80">Balance Check</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-[8px] text-white/50">Helius:</span>
                <span className="text-[9px] font-mono text-emerald-400">{fmtSolUi(heliusBalance)} SOL</span>
              </div>
            </div>
          )}

          <div className="space-y-2">
            <button
              className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-[10px] font-bold hover:bg-emerald-500/20 transition-all"
              onClick={() => window.open('https://pump.fun', '_blank')}
            >
              <ExternalLink className="size-3" />
              Refill on Pump.fun
            </button>
            <button
              className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white/70 text-[10px] font-bold hover:bg-white/10 transition-all"
              onClick={() => setShowRefillOptions(false)}
            >
              <RefreshCw className={`size-3 ${checkingBalances ? 'animate-spin' : ''}`} />
              Refresh Balance
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
